import { Input as AntInput, type InputProps } from 'antd';
import { useId, useMemo, useState } from 'react';
import cn from 'classnames';
import styles from './input.module.scss';

type AppInputProps = InputProps & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
};

export const AppInput = ({
    label,
    placeholder,
    value,
    defaultValue,
    onFocus,
    onBlur,
    required,
    requiredMark,
    footnoteMark,
    className,
    ...rest
}: AppInputProps) => {
    const id = useId();
    const [focused, setFocused] = useState(false);

    const hasValue = useMemo(() => {
        const v = value ?? defaultValue;
        if (Array.isArray(v)) return v.length > 0;
        if (v === 0) return true;
        return v !== undefined && String(v ?? '').trim().length > 0;
    }, [value, defaultValue]);

    const isOccupied = focused || hasValue;

    return (
        <div
            className={cn(styles.root, className, {
                [styles.isFocused]: focused,
                [styles.hasValue]: hasValue,
            })}
        >
            <AntInput
                id={rest.id ?? id}
                {...rest}
                value={value}
                defaultValue={defaultValue}
                bordered={false}
                rootClassName={cn(styles.control, rest.rootClassName)}
                onFocus={e => {
                    setFocused(true);
                    onFocus?.(e);
                }}
                onBlur={e => {
                    setFocused(false);
                    onBlur?.(e);
                }}
            />

            {label && (
                <label
                    htmlFor={rest.id ?? id}
                    className={cn(
                        styles.label,
                        isOccupied ? styles.asLabel : styles.asPlaceholder
                    )}
                >
                    {label}
                    {(required || requiredMark) && (
                        <span className={styles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={styles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
};
